/**
 * Offline Order Service
 * Handles order creation when offline in Electron desktop app
 */

import { v4 as uuidv4 } from 'uuid';

// Generate a local ID with prefix for easy identification
export function generateLocalOrderId(): string {
  return `local_${uuidv4()}`;
}

// Check if an ID is a local (offline-created) ID
export function isLocalId(id: string): boolean {
  return id.startsWith('local_');
}

export interface OfflineOrderInput {
  branchId: string;
  customerName?: string;
  customerPhone?: string;
  customerEmail?: string;
  customerAddress?: string;
  notes?: string;
  orderType?: 'pickup' | 'delivery' | 'dine_in';
  tableNumber?: string;
  deliveryZoneId?: string;
  deliveryFee?: number;
  items: Array<{
    menuItemId: string;
    variantId?: string;
    quantity: number;
    unitPrice: number;
    notes?: string;
    name: string;
  }>;
  customerId?: string;
}

export interface OfflineOrderResult {
  success: boolean;
  order?: {
    id: string;
    branch_id: string;
    order_number: number;
    status: string;
    total_amount: number;
    created_at: string;
    is_offline: boolean;
  };
  error?: string;
}

/**
 * Calculate order totals locally
 * Uses branch settings if available, otherwise defaults
 */
export function calculateOrderTotals(
  items: OfflineOrderInput['items'],
  branchSettings?: {
    taxRate?: number;
    serviceChargeRate?: number;
  },
  deliveryFee: number = 0
): {
  subtotal: number;
  taxAmount: number;
  serviceCharge: number;
  total: number;
} {
  const subtotal = items.reduce(
    (sum, item) => sum + item.unitPrice * item.quantity,
    0
  );
  
  const taxRate = branchSettings?.taxRate ?? 0;
  const serviceChargeRate = branchSettings?.serviceChargeRate ?? 0;
  
  const taxAmount = subtotal * (taxRate / 100);
  const serviceCharge = subtotal * (serviceChargeRate / 100);
  const total = subtotal + taxAmount + serviceCharge + deliveryFee;
  
  return {
    subtotal: Math.round(subtotal * 100) / 100,
    taxAmount: Math.round(taxAmount * 100) / 100,
    serviceCharge: Math.round(serviceCharge * 100) / 100,
    total: Math.round(total * 100) / 100,
  };
}

/**
 * Create an order for offline storage
 * This prepares the order data structure for SQLite insertion
 */
export function prepareOfflineOrder(
  input: OfflineOrderInput,
  branchSettings?: {
    taxRate?: number;
    serviceChargeRate?: number;
  }
): {
  order: Record<string, unknown>;
  items: Array<Record<string, unknown>>;
} {
  const localId = generateLocalOrderId();
  const now = new Date().toISOString();
  
  const { subtotal, taxAmount, serviceCharge, total } = calculateOrderTotals(
    input.items,
    branchSettings,
    input.deliveryFee
  );
  
  const order = {
    id: localId,
    branch_id: input.branchId,
    customer_name: input.customerName || 'Walk-in Customer',
    customer_phone: input.customerPhone || null,
    customer_email: input.customerEmail || null,
    customer_address: input.customerAddress || null,
    customer_id: input.customerId || null,
    table_number: input.tableNumber || null,
    order_type: input.orderType || 'pickup',
    status: 'pending',
    payment_status: 'unpaid',
    subtotal,
    tax_amount: taxAmount,
    service_charge: serviceCharge,
    discount_amount: 0,
    total,
    notes: input.notes || null,
    delivery_zone_id: input.deliveryZoneId || null,
    delivery_fee: input.deliveryFee || 0,
    created_at: now,
    updated_at: now,
    is_created_offline: true,
    is_synced: false,
  };
  
  const items = input.items.map((item, index) => ({
    id: `${localId}_item_${index}`,
    order_id: localId,
    menu_item_id: item.menuItemId,
    variant_id: item.variantId || null,
    name: item.name,
    quantity: item.quantity,
    unit_price: item.unitPrice,
    total_price: item.unitPrice * item.quantity,
    notes: item.notes || null,
    modifiers: null,
    created_at: now,
    updated_at: now,
    is_synced: false,
  }));
  
  return { order, items };
}

/**
 * Validate order data before offline creation
 */
export function validateOfflineOrder(input: OfflineOrderInput): {
  valid: boolean;
  errors: string[];
  warnings: string[];
} {
  const errors: string[] = [];
  const warnings: string[] = [];
  
  // Required fields
  if (!input.branchId) {
    errors.push('Branch ID is required');
  }
  
  if (!input.items || input.items.length === 0) {
    errors.push('At least one item is required');
  }
  
  // Validate items
  input.items?.forEach((item, index) => {
    if (!item.menuItemId) {
      errors.push(`Item ${index + 1}: Menu item ID is required`);
    }
    if (item.quantity <= 0) {
      errors.push(`Item ${index + 1}: Quantity must be positive`);
    }
    if (item.unitPrice < 0) {
      errors.push(`Item ${index + 1}: Price cannot be negative`);
    }
  });
  
  // Delivery validation
  if (input.orderType === 'delivery') {
    if (!input.customerPhone) {
      warnings.push('No phone number provided for delivery order');
    }
    if (!input.customerAddress) {
      warnings.push('No address provided for delivery order');
    }
  }
  
  // Dine-in validation
  if (input.orderType === 'dine_in' && !input.tableNumber) {
    warnings.push('No table number provided for dine-in order');
  }
  
  // Offline-specific warnings
  warnings.push('Order created offline - stock levels not verified');
  warnings.push('Order will sync when connection is restored');
  
  return {
    valid: errors.length === 0,
    errors,
    warnings,
  };
}

export default {
  generateLocalOrderId,
  isLocalId,
  calculateOrderTotals,
  prepareOfflineOrder,
  validateOfflineOrder,
};
